import React, { useState, useEffect, useRef } from "react";
import "./RecWiseCode.css";
import { useNavigate } from 'react-router-dom';
import CTA from './CTA';

const OnboardingModule = () => {
  const [activeSection, setActiveSection] = useState("pre-onboarding");
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionsRef = useRef({});
  const navigate = useNavigate();
  const handleOnboard = () => navigate('/access-recruit');

  const sectionContent = {
    "pre-onboarding": {
      title: "Pre-Onboarding Engagement",
      description: "Engage new hires before day one with automated welcome sequences and digital paperwork. Send personalized welcome emails, company culture videos, and pre-boarding checklists. Collect necessary documents electronically, including tax forms, identification, and banking details. Provide access to employee handbooks, policy documents, and introductory materials to ensure a smooth transition into your organization."
    },
    "digital-documentation": {
      title: "Digital Document & E-Sign",
      description: "Streamline paperwork with secure digital document management and electronic signatures. Create customizable offer letters, contracts, and policy acknowledgments with built-in e-signature capabilities. Maintain version control and track document completion status in real-time. Automate reminders for pending signatures and store all documents in centralized, encrypted repositories for easy access and compliance auditing."
    },
    "equipment-provisioning": {
      title: "Equipment & IT Provisioning",
      description: "Automate hardware and software provisioning for new employees with integrated IT workflows. Coordinate laptop, phone, and equipment assignments through automated request systems. Provision software licenses, email accounts, and system access based on role-specific requirements. Track equipment inventory and setup status while ensuring security compliance through automated access management and permission assignments."
    },
    "orientation-training": {
      title: "Orientation & Training Setup",
      description: "Design structured onboarding programs with customized learning paths and orientation schedules. Create interactive welcome modules, compliance training, and role-specific learning materials. Schedule orientation sessions with key stakeholders and department introductions. Track training completion, assess knowledge retention, and provide feedback mechanisms for continuous improvement of the onboarding experience."
    },
    "buddy-mentor": {
      title: "Buddy & Mentor Assignment",
      description: "Facilitate social integration with intelligent buddy and mentor matching systems. Automatically pair new hires with experienced colleagues based on department, interests, and career goals. Provide structured guidance for buddies with checklists and conversation guides. Track buddy interactions, gather feedback, and measure the effectiveness of mentorship programs in accelerating new hire productivity and engagement."
    }
  };

  const sections = [
    { id: "pre-onboarding", label: "PRE-BOARDING" },
    { id: "digital-documentation", label: "DOCUMENTS" },
    { id: "equipment-provisioning", label: "EQUIPMENT" },
    { id: "orientation-training", label: "TRAINING" },
    { id: "buddy-mentor", label: "BUDDY SYSTEM" }
  ];

  useEffect(() => {
    const onScroll = () => {
      const pos = window.scrollY + 150;
      for (const sec of sections) {
        const el = sectionsRef.current[sec.id];
        if (!el) continue;
        if (pos >= el.offsetTop && pos < el.offsetTop + el.offsetHeight) {
          setActiveSection(sec.id);
          break;
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, en.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15 }
    );
    Object.values(sectionsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const el = sectionsRef.current[id];
    if (el) window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
  };

  return (
    <div className="resto-main-wrapper">
      {/* <nav className="resto-sticky-navbar">
        <div className="resto-nav-container">
          {sections.map((s) => (
            <button
              key={s.id}
              className={`resto-nav-button ${activeSection === s.id ? "resto-active" : ""}`}
              onClick={() => scrollToSection(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </nav> */}

      <div className="resto-content-wrapper">

        {/* 1. Pre-Onboarding */}
        <section
          ref={(el) => (sectionsRef.current["pre-onboarding"] = el)}
          data-section="pre-onboarding"
          className={`resto-section ${visibleSections.has("pre-onboarding") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Pre-Onboarding</div>
            <h2 className="resto-section-title">
              {sectionContent["pre-onboarding"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["pre-onboarding"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleOnboard}>Setup Pre-boarding</button>
              <button className="resto-secondary-btn" onClick={handleOnboard}>View Candidates</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/Automated-workflows-1.png" className="resto-real-image" alt="Pre-Onboarding" />
          </div>
        </section>

        {/* 2. Digital Documentation */}
        <section
          ref={(el) => (sectionsRef.current["digital-documentation"] = el)}
          data-section="digital-documentation"
          className={`resto-section resto-reverse ${visibleSections.has("digital-documentation") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Documentation</div>
            <h2 className="resto-section-title">
              {sectionContent["digital-documentation"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["digital-documentation"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleOnboard}>Create Documents</button>
              <button className="resto-secondary-btn" onClick={handleOnboard}>Track Signatures</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/eSign-1.png" className="resto-real-image" alt="Digital Documentation" />
          </div>
        </section>

        <CTA/>

        {/* 3. Equipment Provisioning */}
        <section
          ref={(el) => (sectionsRef.current["equipment-provisioning"] = el)}
          data-section="equipment-provisioning"
          className={`resto-section ${visibleSections.has("equipment-provisioning") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">IT Provisioning</div>
            <h2 className="resto-section-title">
              {sectionContent["equipment-provisioning"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["equipment-provisioning"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleOnboard}>Request Equipment</button>
              <button className="resto-secondary-btn" onClick={handleOnboard}>Track Setup</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/Docs-management-1.png" className="resto-real-image" alt="Equipment Provisioning" />
          </div>
        </section>

        {/* 4. Orientation & Training */}
        <section
          ref={(el) => (sectionsRef.current["orientation-training"] = el)}
          data-section="orientation-training"
          className={`resto-section resto-reverse ${visibleSections.has("orientation-training") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-right">
            <div className="resto-section-label">Training</div>
            <h2 className="resto-section-title">
              {sectionContent["orientation-training"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["orientation-training"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleOnboard}>Design Program</button>
              <button className="resto-secondary-btn" onClick={handleOnboard}>Track Progress</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-left">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/Docs-management-1.png" className="resto-real-image" alt="Orientation Training" />
          </div>
        </section>

        {/* 5. Buddy & Mentor */}
        <section
          ref={(el) => (sectionsRef.current["buddy-mentor"] = el)}
          data-section="buddy-mentor"
          className={`resto-section ${visibleSections.has("buddy-mentor") ? "resto-visible" : ""}`}
        >
          <div className="resto-section-content resto-fade-in-left">
            <div className="resto-section-label">Buddy System</div>
            <h2 className="resto-section-title">
              {sectionContent["buddy-mentor"].title}
            </h2>
            <p className="resto-section-description">
              {sectionContent["buddy-mentor"].description}
            </p>

            <div className="resto-buttons-group">
              <button className="resto-primary-btn" onClick={handleOnboard}>Assign Buddies</button>
              <button className="resto-secondary-btn" onClick={handleOnboard}>Monitor Pairs</button>
            </div>
          </div>

          <div className="resto-section-image resto-fade-in-right">
            <img src="https://res.cloudinary.com/www-hibob-com/fl_lossy,f_webp,q_auto/wp-website/uploads/Docs-management-1.png" className="resto-real-image" alt="Buddy System" />
          </div>
        </section>

      </div>
    </div>
  );
};

export default OnboardingModule;